(function () {
  'use strict';

  if (customElements.get('predictive-search')) return;

  var DEBOUNCE_MS = 250;
  var MIN_QUERY_LENGTH = 1;
  var PAGE_SIZE = 12;
  var STRINGS_FALLBACK = {
    suggestions: 'Popular Suggestions',
    categories: 'Categories',
    products: 'Products',
    no_results: 'No results found.',
    view_all_n: 'View All __CMQUERY_COUNT__ Items',
    view_all: 'View All',
  };

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function moneyUSD(cents) {
    if (cents == null) return '';
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Convermax product images sometimes have width hints; if missing use as-is.
  function imgUrl(item) {
    return item.image || item.image2 || '';
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('.search-result-container');
      this.cmStore = this.dataset.cmStore || '';
      this.cmEndpoint = this.cmStore
        ? 'https://' + this.cmStore + '.myconvermax.com/autocomplete.json'
        : '';
      this.lastQuery = '';
      this.abortController = null;

      // Try to read translation strings from the data-island the snippet emits, if any.
      var dataIsland = this.querySelector('script[type="application/json"][data-strings]');
      this.strings = Object.assign({}, STRINGS_FALLBACK);
      if (dataIsland) {
        try { Object.assign(this.strings, JSON.parse(dataIsland.textContent)); } catch (e) {}
      }

      if (!this.input || !this.results || !this.cmEndpoint) return;

      this._onInputDebounced = debounce(this._onInput.bind(this), DEBOUNCE_MS);
      this.input.addEventListener('input', this._onInputDebounced);
      this.input.addEventListener('focus', this._onFocus.bind(this));
      this.input.addEventListener('keydown', this._onKeydown.bind(this));
      document.addEventListener('click', this._onDocumentClick.bind(this));
    }

    _onInput() {
      var q = this.input.value.trim();
      if (q.length < MIN_QUERY_LENGTH) {
        this.close();
        this.lastQuery = '';
        return;
      }
      if (q === this.lastQuery) return;
      this.lastQuery = q;
      this._fetch(q);
    }

    _onFocus() {
      var q = this.input.value.trim();
      if (q.length >= MIN_QUERY_LENGTH) {
        if (this.lastQuery === q && this.results.innerHTML.trim()) {
          this.classList.add('show');
        } else {
          this._fetch(q);
        }
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    }

    _onDocumentClick(e) {
      if (!this.contains(e.target)) this.close();
    }

    _fetch(query) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      var url = this.cmEndpoint
        + '?query=' + encodeURIComponent(query)
        + '&pageSize=' + PAGE_SIZE;

      this.classList.add('is-searching');

      fetch(url, { signal: this.abortController.signal })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (items) {
          this._render(query, items);
          this.classList.remove('is-searching');
          this.classList.add('show');
        }.bind(this))
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          console.error('predictive-search:', err);
          this.classList.remove('is-searching');
        }.bind(this));
    }

    _render(query, items) {
      if (!Array.isArray(items) || items.length === 0) {
        this.results.innerHTML =
          '<div class="predictive__search-result">' +
            '<div class="predictive__search-result-inner">' +
              '<div class="no-result">' + escapeHtml(this.strings.no_results) + '</div>' +
            '</div>' +
          '</div>';
        return;
      }

      // Group by Type. Convermax returns Type ∈ {Freetext, Product, Category}.
      var freetext = [];
      var products = [];
      var categories = [];
      var totalHits = 0;

      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (it.Type === 'Freetext') freetext.push(it);
        else if (it.Type === 'Category') categories.push(it);
        else if (it.Type === 'Product') {
          products.push(it);
          // TotalHits is on each Product; take first non-zero.
          if (!totalHits && it.TotalHits) totalHits = it.TotalHits;
        }
      }

      // If no products in autocomplete payload, fall back to query length so the
      // count display still reads sensibly. Real count fetch (search.json) would
      // be a follow-up if Akshay needs it on no-product queries.
      if (!totalHits && products.length === 0 && (freetext.length || categories.length)) {
        totalHits = 0;
      }

      var leftHtml = '';
      if (freetext.length) {
        leftHtml += this._groupHtml(this.strings.suggestions, this._freetextItems(freetext, query), 'queries');
      }
      if (categories.length) {
        leftHtml += this._groupHtml(this.strings.categories, this._categoryItems(categories), 'collections');
      }

      // View All CTA — uses Convermax.createSearchUrl when available so URLs match
      // the canonical format Convermax itself emits (handles spacing/encoding).
      var viewAllHref = this._viewAllUrl(query);
      var viewAllLabel = totalHits > 0
        ? this.strings.view_all_n.replace('__CMQUERY_COUNT__', totalHits).replace('{count}', totalHits)
        : this.strings.view_all;
      leftHtml += '<div class="predictive__search-view-all">' +
        '<a href="' + escapeHtml(viewAllHref) + '" class="btn-view-all">' +
          escapeHtml(viewAllLabel) +
        '</a></div>';

      var rightHtml = '';
      if (products.length) {
        rightHtml = this._productsHtml(products);
      }

      this.results.innerHTML =
        '<div class="predictive__search-result">' +
        '<div class="predictive__search-result-inner">' +
          '<div class="predictive-search__columns">' +
            (leftHtml ? '<div class="predictive-search__col-left">' + leftHtml + '</div>' : '') +
            (rightHtml ? '<div class="predictive-search__col-right">' + rightHtml + '</div>' : '') +
          '</div>' +
        '</div>' +
        '</div>';
    }

    _viewAllUrl(query) {
      // Prefer Convermax.createSearchUrl if available — gives canonical URL format.
      if (window.Convermax && typeof window.Convermax.createSearchUrl === 'function') {
        try {
          var u = window.Convermax.createSearchUrl({ query: query });
          if (u) return u;
        } catch (e) {}
      }
      // Fallback: send both q= and query= so the page works either way (q= is
      // mirrored from the input field; query= is the Convermax keyword param).
      var enc = encodeURIComponent(query);
      return '/search?q=' + enc + '&query=' + enc;
    }

    _groupHtml(heading, listHtml, ariaKey) {
      return '<div class="predictive__search-group predictive__search-group--' + ariaKey + '">' +
        '<h5>' + escapeHtml(heading) + '</h5>' +
        '<ul role="group" aria-labelledby="predictive-search-' + ariaKey + '">' +
          listHtml +
        '</ul></div>';
    }

    _freetextItems(items, query) {
      var icon = '<span class="predictive__search-query-icon" aria-hidden="true">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
      '</span>';
      var out = '';
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var href = this._viewAllUrl(it.Text || it.Term || query);
        // TextHighlight already contains <b>..</b> markup; render as HTML.
        var label = it.TextHighlight || escapeHtml(it.Text || it.Term || '');
        out += '<li role="option"><a href="' + escapeHtml(href) + '" tabindex="-1">' +
          icon + '<p>' + label + '</p>' +
        '</a></li>';
      }
      return out;
    }

    _categoryItems(items) {
      var out = '';
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var href = it.url || '#';
        var label = it.TextHighlight || escapeHtml(it.title || it.Text || '');
        out += '<li role="option"><a href="' + escapeHtml(href) + '" tabindex="-1">' +
          '<p>' + label + '</p>' +
        '</a></li>';
      }
      return out;
    }

    _productsHtml(items) {
      var cards = '';
      for (var i = 0; i < items.length && i < 6; i++) {
        var it = items[i];
        var url = it.url || ('/products/' + (it.handle || ''));
        var img = imgUrl(it);
        // Title may contain Convermax's <b>..</b> highlight tags; trusted source.
        var titleHtml = it.title || it.Text || '';
        // Convermax returns price in major units (e.g. 535 = $535.00). moneyUSD formats the
        // number directly with two decimals, so pass it.price as-is.
        var price = moneyUSD(it.price);

        cards += '<li role="option" class="predictive__product-card">' +
          '<a href="' + escapeHtml(url) + '" tabindex="-1" class="predictive__product-link">' +
            '<div class="predictive__product-image">' +
              (img
                ? '<img src="' + escapeHtml(img) + '" loading="lazy" alt="' + escapeHtml(it.title || '') + '">'
                : '<div class="predictive__product-image--placeholder"></div>'
              ) +
            '</div>' +
            '<div class="predictive__product-title"><p>' + titleHtml + '</p></div>' +
            '<div class="predictive__product-price"><span class="price">' + escapeHtml(price) + '</span></div>' +
          '</a>' +
        '</li>';
      }
      return '<div class="predictive__search-group predictive__search-group--products">' +
        '<h5>' + escapeHtml(this.strings.products) + '</h5>' +
        '<ul role="group" aria-labelledby="predictive-search-products" class="predictive__products-grid">' +
          cards +
        '</ul></div>';
    }

    open() {
      var q = this.input ? this.input.value.trim() : '';
      if (q.length >= MIN_QUERY_LENGTH) this.classList.add('show');
    }

    close() {
      this.classList.remove('show');
      this.classList.remove('is-searching');
    }

    focusSearch() {
      if (this.input) this.input.focus();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
